// ============================================================================
// OVERSELL AUTHORIZATION DIALOG
// ============================================================================
// Confirms accepting a booking BEYOND the property's available inventory.
// See crs-doc/CRS-Inventory-Shortage-and-Oversell-Design.md §7.1, BR-SHORT-003.
//
// Not a plain "are you sure". The property is committing to a room it does not
// have, on the expectation that cancellations will cover it, and the reason is
// mandatory because the exception log this writes is only worth keeping if it
// says why. That log is what the shortage board reads.
//
// The dialog is only reachable when the SERVER has already refused with an
// availability conflict and the user holds the `oversell` action. Confirming
// re-submits the same booking with an explicit authorisation; the server then
// re-checks policy, cap, cutoff and permission and may still refuse.

import React, { useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';

interface OversellAuthorizationDialogProps {
  isOpen: boolean;
  /** The server's refusal, verbatim — it names the category, the shortfall and the limit. */
  message: string;
  submitting?: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

const OversellAuthorizationDialog: React.FC<OversellAuthorizationDialogProps> = ({
  isOpen, message, submitting = false, onConfirm, onCancel,
}) => {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setTouched(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const trimmed = reason.trim();
  const reasonError =
    trimmed.length < 3
      ? 'A reason is required (at least 3 characters).'
      : trimmed.length > 500
        ? 'Reason must be 500 characters or fewer.'
        : '';

  const handleConfirm = () => {
    setTouched(true);
    if (reasonError) return;
    onConfirm(trimmed);
  };

  return (
    <>
      {/* Stacked ABOVE the booking modal that opens it, matching ConfirmDialog's
          levels — without an explicit z-index this shares `--z-modal` with its
          parent and interleaves with it.

          `.modal-container` is already `position: fixed`, which both centres the
          dialog on the viewport AND acts as the containing block for the
          absolutely-positioned loading overlay below. Overriding it to
          `relative` (as an earlier revision did) dropped the dialog into the
          booking modal's flow, where its parent's `overflow: hidden` clipped the
          footer — the Cancel / Accept buttons went off-screen. */}
      <div className="modal-backdrop" style={{ zIndex: 10060 }} />
      <div className="modal-container modal-sm" style={{ zIndex: 10070 }}>
        {submitting && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#dc2626', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              Saving…
            </span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title" style={{ color: 'var(--danger, #dc2626)' }}>
            Accept booking beyond available inventory?
          </h2>
          <button className="modal-close" onClick={onCancel} disabled={submitting}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <p style={{ fontSize: '0.875rem', color: 'var(--text-primary)', marginTop: 0 }}>
            {message}
          </p>

          <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
            The property will owe rooms it does not currently have. The shortage appears
            on the Shortage Board immediately, where it must be managed before arrival —
            by upgrading, relocating, consolidating or walking a guest.
          </p>

          <div className="form-group" data-field="oversellReason">
            <label className="form-label">
              Why is the property accepting this? <span className="text-danger">*</span>
            </label>
            <textarea
              className={`form-textarea${touched && reasonError ? ' form-field-error' : ''}`}
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
              maxLength={500}
              placeholder="e.g. group release expected — 4 rooms held by an agent who has not paid"
            />
            {touched && reasonError
              ? <div className="form-error-message">{reasonError}</div>
              : (
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                  Recorded against every night this commits, and shown on the shortage board.
                </div>
              )}
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
          <button className="btn btn-danger" onClick={handleConfirm} disabled={submitting}>
            Accept and save
          </button>
        </div>
      </div>
    </>
  );
};

export default OversellAuthorizationDialog;
